import { Component, Input } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { CardType } from '../../models/common';
import { CurrencyFormatterService } from 'src/app/shared/services/currency-formatter.service';

@Component({
  selector: 'app-card-ui',
  templateUrl: './card-ui.component.html',
  styleUrls: ['./card-ui.component.css'],
})
export class CardUiComponent {
  @Input() items: CardType<any>[] = [];
  effect = 'scrollx';

  // < 500px (the app's md breakpoint) abbreviates large values from a million;
  // larger screens only abbreviate from a billion.
  private isMobile = false;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private currencyFormatter: CurrencyFormatterService,
  ) {
    this.breakpointObserver
      .observe('(max-width: 499.98px)')
      .subscribe((res) => (this.isMobile = res.matches));
  }

  public get isLoading() {
    return !this.items.find((val) => !val.isFetching);
  }

  private toNumber(value: number | string): number {
    return typeof value === 'string' ? parseFloat(value) : value;
  }

  /**
   * Whether a card value is large enough to be abbreviated (so it doesn't
   * stretch the card). Only applies to currency/number values.
   * Threshold: 1,000,000 on mobile, 1,000,000,000 on larger screens.
   */
  public isAbbreviated(item: CardType<any>): boolean {
    if (item.pipeType !== 'currency' && item.pipeType !== 'number') return false;
    const num = this.toNumber(item.value);
    if (isNaN(num)) return false;
    const threshold = this.isMobile ? 1_000_000 : 1_000_000_000;
    return Math.abs(num) >= threshold;
  }

  /** Abbreviated form, e.g. 26,985,649 -> "₦26.99M", 5,000,000,000 -> "₦5B". */
  public abbreviatedValue(item: CardType<any>): string {
    const num = this.toNumber(item.value);
    const abs = Math.abs(num);

    let divisor: number;
    let suffix: string;
    if (abs >= 1e12) {
      divisor = 1e12;
      suffix = 'T';
    } else if (abs >= 1e9) {
      divisor = 1e9;
      suffix = 'B';
    } else {
      divisor = 1e6;
      suffix = 'M';
    }

    // up to 2 decimals, trimming trailing zeros (27.00 -> 27, 26.90 -> 26.9)
    const short = (num / divisor).toFixed(2).replace(/\.?0+$/, '');
    const symbol =
      item.pipeType === 'currency'
        ? (item.currencySymbol ?? this.currencyFormatter.getCurrencySymbol())
        : '';

    return `${symbol}${short}${suffix}`;
  }

  /** Full formatted value using the item's own currency symbol (or tenant default). */
  public formattedCurrencyValue(item: CardType<any>): string {
    const symbol = item.currencySymbol ?? this.currencyFormatter.getCurrencySymbol();
    const num = this.toNumber(item.value);
    if (isNaN(num)) return `${symbol}0.00`;
    return symbol + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
}
